import React, { useState } from "react";
import { Modal } from "semantic-ui-react";
import Select from 'react-select';
import ProgressLoader from "../../Common/ProgressLoader";
import BasicButton from '../../Common/Button';
import { getParsedList } from '../../../Utils/helpers';

interface BulkAddRMsModalProps {
    open: boolean;
    loading: boolean;
    selectedOffices: any[];
    allRmUsers: any[];
    handleCancel: () => void;
    handleApply: (addRmIds: number[]) => Promise<{ results: any[]; invalid_rms: any[] } | null>;
    config?: any;
}

type Step = 'select' | 'preview' | 'result';

// Offices are identified to users by their office code (e.g. "OF1451") and/or name —
// never the internal numeric database id, which means nothing to anyone outside the DB.
const officeLabel = (office: any) => {
    if (!office) return '';
    if (office.office_code && office.office_name) return `${office.office_code} — ${office.office_name}`;
    return office.office_code || office.office_name || '';
};

const BulkAddRMsModal = (props: BulkAddRMsModalProps) => {
    const { open, loading, selectedOffices, allRmUsers, handleCancel, handleApply, config } = props;
    const [step, setStep] = useState<Step>('select');
    const [selectedAddRMs, setSelectedAddRMs] = useState<any[]>([]);
    const [results, setResults] = useState<any[]>([]);
    const [invalidRms, setInvalidRms] = useState<any[]>([]);

    const addRmIds: number[] = selectedAddRMs.map((item: any) => Number(item.user_id));
    const hasSelection = addRmIds.length > 0;

    // Only ADDS RMs — never removes or overwrites RMs an office already has.
    const officePreview = selectedOffices.map((office: any) => {
        const currentRmIds: number[] = (getParsedList(office.allowed_rm_ids) || []).map(Number);
        const newRms = selectedAddRMs.filter((rm: any) => !currentRmIds.includes(Number(rm.user_id)));
        return {
            office_id: office.office_id,
            office_label: officeLabel(office),
            newRmNames: newRms.map((rm: any) => rm.label),
            noChange: newRms.length === 0,
        };
    });

    const resetState = () => {
        setStep('select');
        setSelectedAddRMs([]);
        setResults([]);
        setInvalidRms([]);
    };

    const handleClose = () => {
        resetState();
        handleCancel();
    };

    const handleConfirmApply = async () => {
        const response = await handleApply(addRmIds);
        // null means the request failed — the error is already shown via the page-level
        // overlay, so stay on this step rather than showing a misleading empty "complete".
        if (response === null) return;
        setResults(response.results || []);
        setInvalidRms(response.invalid_rms || []);
        setStep('result');
    };

    return (
        <Modal
            centered
            size='small'
            open={open}
            onClose={handleClose}
            closeOnEscape={false}
            closeOnDimmerClick={false}
        >
            <Modal.Content>
                <div className="close-button">
                    <button className="email-activity-close-button" onClick={handleClose}>X</button>
                </div>
                <ProgressLoader loading={loading} size='small' />

                {step === 'select' && (
                    <div className="user-form">
                        <h3>Bulk Add RMs — {selectedOffices.length} {selectedOffices.length > 1 ? 'offices' : 'office'} selected</h3>
                        <p>RMs selected here are added to each office&apos;s existing RM list. Nothing already assigned is removed or changed.</p>
                        <div className="fields">
                            <div className="four wide field"><p>Add RM(s) :</p></div>
                            <div className="twelve wide field">
                                <Select
                                    isMulti={true}
                                    options={allRmUsers}
                                    value={selectedAddRMs}
                                    onChange={(value: any) => setSelectedAddRMs(value || [])}
                                    placeholder='Select RM(s) to add'
                                />
                            </div>
                        </div>
                    </div>
                )}

                {step === 'preview' && (
                    <div className="user-form">
                        <h3>Preview changes</h3>
                        <p>Review what will change before applying.</p>
                        <table className="ui celled table">
                            <thead>
                                <tr>
                                    <th>Office</th>
                                    <th>RMs to add</th>
                                </tr>
                            </thead>
                            <tbody>
                                {officePreview.map((row: any) => (
                                    <tr key={row.office_id}>
                                        <td>{row.office_label}</td>
                                        <td>
                                            {row.noChange
                                                ? <span style={{ color: '#8A9490' }}>No change — already has all selected RMs</span>
                                                : row.newRmNames.join(', ')}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {step === 'result' && (
                    <div className="user-form">
                        <h3>Bulk update complete</h3>
                        {invalidRms.length > 0 && (
                            <div style={{ background: '#F7E6E3', color: '#B3392C', padding: '10px 14px', borderRadius: '6px', marginBottom: '14px' }}>
                                ⚠ {invalidRms.length} selected {invalidRms.length > 1 ? 'users were' : 'user was'} skipped — not a Relationship Manager for this client, so nothing was added for {invalidRms.length > 1 ? 'them' : 'that user'}.
                            </div>
                        )}
                        <table className="ui celled table">
                            <thead>
                                <tr>
                                    <th>Office</th>
                                    <th>Result</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((row: any) => {
                                    const office = selectedOffices.find((o: any) => o.office_id === row.office_id);
                                    return (
                                        <tr key={row.office_id}>
                                            <td>{officeLabel(office)}</td>
                                            <td style={{ color: row.success ? '#2E7D46' : '#B3392C' }}>
                                                {row.success ? 'Updated' : `Failed: ${row.error}`}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal.Content>
            <Modal.Actions>
                {step === 'select' && (
                    <>
                        <BasicButton
                            textColor={config?.client_config_fe?.button_color}
                            backgroundColor={'#FFFFFF'}
                            size='18px'
                            buttonText="CANCEL"
                            className="save-button button-border"
                            onClick={handleClose}
                        />
                        <button
                            className={hasSelection ? "save-button button-border" : "not-active save-button button-border"}
                            style={{ background: config?.client_config_fe?.button_color }}
                            onClick={() => hasSelection && setStep('preview')}
                        >PREVIEW</button>
                    </>
                )}
                {step === 'preview' && (
                    <>
                        <BasicButton
                            textColor={config?.client_config_fe?.button_color}
                            backgroundColor={'#FFFFFF'}
                            size='18px'
                            buttonText="BACK"
                            className="save-button button-border"
                            onClick={() => setStep('select')}
                        />
                        <button
                            className="save-button button-border"
                            style={{ background: config?.client_config_fe?.button_color }}
                            onClick={handleConfirmApply}
                        >CONFIRM &amp; APPLY</button>
                    </>
                )}
                {step === 'result' && (
                    <button
                        className="save-button button-border"
                        style={{ background: config?.client_config_fe?.button_color }}
                        onClick={handleClose}
                    >DONE</button>
                )}
            </Modal.Actions>
        </Modal>
    );
};

export default BulkAddRMsModal;
